import Phaser from 'phaser';
import { MAP_W, MAP_H, TILE } from '../config';
import type { District } from '../types';

export const T = {
  GRASS: 0,
  ROAD: 1,
  ROAD_LINE: 2,
  PAVEMENT: 3,
  WATER: 4,
  PITCH: 5,
  STADIUM: 6,
  WAREHOUSE: 7,
  BUILDING_A: 8,
  BUILDING_B: 9,
  BUILDING_C: 10
} as const;

const TEX: Record<number, string> = {
  [T.GRASS]: 'tile_grass',
  [T.ROAD]: 'tile_road',
  [T.ROAD_LINE]: 'tile_road_line',
  [T.PAVEMENT]: 'tile_pavement',
  [T.WATER]: 'tile_water',
  [T.PITCH]: 'tile_pitch',
  [T.STADIUM]: 'tile_stadium',
  [T.WAREHOUSE]: 'tile_warehouse',
  [T.BUILDING_A]: 'tile_building_a',
  [T.BUILDING_B]: 'tile_building_b',
  [T.BUILDING_C]: 'tile_building_c'
};

/** Frame indices in the 'cityatlas' spritesheet (37 cols × 28 rows of 16×16). null = use procedural TEX. */
const ATLAS_FRAME: Record<number, number | null> = {
  [T.GRASS]: null,
  [T.ROAD]: 714,          // plain dark asphalt — no markings
  [T.ROAD_LINE]: 712,     // asphalt + white centre dash (UK style)
  [T.PAVEMENT]: 705,      // light concrete pavement
  [T.WATER]: null,
  [T.PITCH]: null,
  [T.STADIUM]: 1,         // brown brick — Anfield stand
  [T.WAREHOUSE]: 4,       // grey concrete — dock industrial
  [T.BUILDING_A]: 11,     // concrete office with window detail
  [T.BUILDING_B]: 7,      // cream sandstone — Victorian commercial
  [T.BUILDING_C]: 1       // brown brick — terraced housing
};

const SOLID = new Set<number>([
  T.WATER, T.STADIUM, T.WAREHOUSE, T.BUILDING_A, T.BUILDING_B, T.BUILDING_C
]);

const DRIVABLE = new Set<number>([T.ROAD, T.ROAD_LINE]);
const WALKABLE_PAV = new Set<number>([T.PAVEMENT]);

function isRoadId(t: number): boolean { return t === T.ROAD || t === T.ROAD_LINE; }

export class LiverpoolMap {
  readonly grid: number[][];
  readonly w = MAP_W;
  readonly h = MAP_H;
  readonly tile = TILE;
  private solidGroup!: Phaser.Physics.Arcade.StaticGroup;

  constructor() {
    this.grid = generate();
  }

  build(scene: Phaser.Scene): Phaser.Physics.Arcade.StaticGroup {
    // Render every tile as an image (small map ok at 80×60 = 4800 tiles)
    for (let y = 0; y < this.h; y++) {
      for (let x = 0; x < this.w; x++) {
        const id = this.grid[y][x];
        const frame = ATLAS_FRAME[id];
        const cx = x * TILE + TILE / 2;
        const cy = y * TILE + TILE / 2;
        if (frame != null) {
          const img = scene.add.image(cx, cy, 'cityatlas', frame).setDisplaySize(TILE, TILE);
          if (id === T.ROAD_LINE) {
            // Determine orientation from neighbouring road tiles.
            const left  = x > 0           && isRoadId(this.grid[y][x - 1]);
            const right = x < this.w - 1  && isRoadId(this.grid[y][x + 1]);
            const up    = y > 0           && isRoadId(this.grid[y - 1][x]);
            const down  = y < this.h - 1  && isRoadId(this.grid[y + 1][x]);
            const vertical = up && down && !(left && right);
            if (vertical) img.setAngle(90);
          }
        } else {
          scene.add.image(cx, cy, TEX[id]);
        }
      }
    }

    // Build static collision bodies for solid tiles (merged horizontally for efficiency)
    this.solidGroup = scene.physics.add.staticGroup();
    for (let y = 0; y < this.h; y++) {
      let runStart = -1;
      for (let x = 0; x <= this.w; x++) {
        const solid = x < this.w && SOLID.has(this.grid[y][x]);
        if (solid && runStart < 0) runStart = x;
        if ((!solid || x === this.w) && runStart >= 0) {
          const len = x - runStart;
          const cx = runStart * TILE + (len * TILE) / 2;
          const cy = y * TILE + TILE / 2;
          const rect = scene.add.rectangle(cx, cy, len * TILE, TILE).setVisible(false);
          scene.physics.add.existing(rect, true);
          this.solidGroup.add(rect);
          runStart = -1;
        }
      }
    }
    return this.solidGroup;
  }

  /** World rect of the map */
  bounds() { return new Phaser.Geom.Rectangle(0, 0, this.w * TILE, this.h * TILE); }

  tileAt(worldX: number, worldY: number): number {
    const tx = Math.floor(worldX / TILE);
    const ty = Math.floor(worldY / TILE);
    if (tx < 0 || ty < 0 || tx >= this.w || ty >= this.h) return T.WATER;
    return this.grid[ty][tx];
  }

  isDrivable(wx: number, wy: number) { return DRIVABLE.has(this.tileAt(wx, wy)); }
  isWalkable(wx: number, wy: number) {
    const t = this.tileAt(wx, wy);
    return !SOLID.has(t);
  }
  isPavement(wx: number, wy: number) { return WALKABLE_PAV.has(this.tileAt(wx, wy)); }

  /** BFS outward from a world-coord seed to the nearest non-solid (walkable) tile centre. */
  nearestWalkable(wx: number, wy: number): { x: number; y: number } {
    const sx = Math.floor(wx / TILE);
    const sy = Math.floor(wy / TILE);
    const seen = new Set<number>();
    const q: [number, number][] = [[sx, sy]];
    seen.add(sy * this.w + sx);
    while (q.length) {
      const [x, y] = q.shift()!;
      if (x >= 0 && y >= 0 && x < this.w && y < this.h && !SOLID.has(this.grid[y][x])) {
        return { x: x * TILE + TILE / 2, y: y * TILE + TILE / 2 };
      }
      for (const [dx, dy] of [[1,0],[-1,0],[0,1],[0,-1]]) {
        const nx = x + dx, ny = y + dy;
        const k = ny * this.w + nx;
        if (nx < 0 || ny < 0 || nx >= this.w || ny >= this.h || seen.has(k)) continue;
        seen.add(k);
        q.push([nx, ny]);
      }
    }
    return { x: wx, y: wy };
  }

  /** Pick a random pavement world position */
  randomPavement(rng: Phaser.Math.RandomDataGenerator): { x: number; y: number } {
    for (let i = 0; i < 200; i++) {
      const tx = rng.between(2, this.w - 3);
      const ty = rng.between(2, this.h - 3);
      if (this.grid[ty][tx] === T.PAVEMENT) {
        return { x: tx * TILE + TILE / 2, y: ty * TILE + TILE / 2 };
      }
    }
    return { x: TILE * 10, y: TILE * 26 };
  }

  /** Pick a random road position */
  randomRoad(rng: Phaser.Math.RandomDataGenerator): { x: number; y: number } {
    for (let i = 0; i < 200; i++) {
      const tx = rng.between(2, this.w - 3);
      const ty = rng.between(2, this.h - 3);
      if (DRIVABLE.has(this.grid[ty][tx])) {
        return { x: tx * TILE + TILE / 2, y: ty * TILE + TILE / 2 };
      }
    }
    return { x: TILE * 18, y: TILE * 30 };
  }

  /** Human-readable district name for HUD */
  areaAt(wx: number, wy: number): District {
    const tx = Math.floor(wx / TILE);
    const ty = Math.floor(wy / TILE);
    if (tx < 7) return 'Mersey';
    if (ty < 14 && tx >= 22 && tx <= 36) return 'Anfield';
    if (ty < 14) return 'Bootle';
    if (ty >= 35 && tx <= 18) return 'Albert Dock';
    if (ty >= 42 && tx >= 55) return 'Sefton Park';
    if (ty >= 42) return 'Toxteth';
    if (tx >= 7 && tx <= 11 && ty >= 22 && ty <= 32) return 'Pier Head';
    return 'City Centre';
  }
}

/* -------------------- generation -------------------- */

interface Rect { x: number; y: number; w: number; h: number }

interface DistrictSpec {
  name: string;
  rect: Rect;
  palette: number[];
  minBlock: number;
  maxBlock: number;
}

const DISTRICTS: DistrictSpec[] = [
  // Bootle (north): tighter terraces
  { name: 'Bootle', rect: { x: 7, y: 0, w: 73, h: 14 },
    palette: [T.BUILDING_C, T.BUILDING_B], minBlock: 3, maxBlock: 8 },
  // City Centre: bigger commercial blocks, mixed
  { name: 'CityCentre', rect: { x: 12, y: 14, w: 68, h: 21 },
    palette: [T.BUILDING_A, T.BUILDING_B, T.BUILDING_C], minBlock: 4, maxBlock: 14 },
  // Albert Dock: long warehouse blocks
  { name: 'AlbertDock', rect: { x: 7, y: 35, w: 12, h: 25 },
    palette: [T.WAREHOUSE], minBlock: 5, maxBlock: 14 },
  // Toxteth: dense terraced housing — many small narrow blocks
  { name: 'Toxteth', rect: { x: 19, y: 36, w: 35, h: 24 },
    palette: [T.BUILDING_C, T.BUILDING_B], minBlock: 3, maxBlock: 7 },
  // Sefton Park kept as grass — sparse pavilions added below.
];

function generate(): number[][] {
  const g: number[][] = Array.from({ length: MAP_H }, () => new Array(MAP_W).fill(T.GRASS));
  const rng = mulberry32(0x113E12); // "LIVE" vibes — deterministic seed

  // Mersey strip (west)
  for (let y = 0; y < MAP_H; y++)
    for (let x = 0; x < 7; x++) g[y][x] = T.WATER;

  // Pier Head dock area (always pavement)
  for (let y = 22; y <= 32; y++)
    for (let x = 7; x <= 11; x++) g[y][x] = T.PAVEMENT;

  // BSP per district
  for (const d of DISTRICTS) fillDistrict(g, d, rng);

  // Anfield: overlay stadium with pitch + south entrance
  const stadium: Rect = { x: 24, y: 3, w: 12, h: 9 };
  for (let y = stadium.y; y < stadium.y + stadium.h; y++)
    for (let x = stadium.x; x < stadium.x + stadium.w; x++)
      if (x >= 0 && y >= 0 && x < MAP_W && y < MAP_H) g[y][x] = T.STADIUM;
  for (let y = stadium.y + 2; y < stadium.y + stadium.h - 2; y++)
    for (let x = stadium.x + 2; x < stadium.x + stadium.w - 2; x++) g[y][x] = T.PITCH;
  // Punch a 2-tile wide pavement entrance through the south stand to the pitch
  const entryX = stadium.x + Math.floor(stadium.w / 2) - 1;
  for (let x = entryX; x <= entryX + 1; x++) {
    for (let y = stadium.y + stadium.h - 1; y >= stadium.y + stadium.h - 3; y--) g[y][x] = T.PAVEMENT;
  }
  for (let y = stadium.y + stadium.h; y <= stadium.y + stadium.h + 1 && y < MAP_H; y++) {
    for (let x = entryX; x <= entryX + 1; x++) g[y][x] = T.PAVEMENT;
  }

  // Sefton Park: grass + a couple of pavilion blocks
  scatterPavilions(g, { x: 56, y: 42, w: 23, h: 17 }, rng, 3);

  // Pier Head connector — punch a road from dock pavement east into City Centre
  for (let y = 27; y <= 28; y++)
    for (let x = 11; x <= 14; x++)
      if (g[y][x] !== T.WATER) g[y][x] = T.ROAD;

  // Strand Road: continuous north-south coastal road on x=11 connecting Bootle→Pier→Dock
  for (let y = 0; y < MAP_H; y++) {
    if (g[y][11] !== T.WATER && g[y][11] !== T.PAVEMENT) g[y][11] = T.ROAD;
  }

  // Centre-line dashes on long straight road segments (every 2nd tile)
  paintCentreLines(g);

  return g;
}

function fillDistrict(g: number[][], d: DistrictSpec, rng: () => number) {
  // Fill district interior with ROAD; BSP blocks then carve out pavement+building footprints.
  for (let y = d.rect.y; y < d.rect.y + d.rect.h; y++) {
    for (let x = d.rect.x; x < d.rect.x + d.rect.w; x++) {
      if (x < 0 || y < 0 || x >= MAP_W || y >= MAP_H) continue;
      if (g[y][x] === T.GRASS) g[y][x] = T.ROAD;
    }
  }

  const blocks: Rect[] = [];
  bsp({ ...d.rect }, d.minBlock, d.maxBlock, rng, blocks);

  for (const b of blocks) {
    if (b.w < 2 || b.h < 2) continue;
    for (let y = b.y; y < b.y + b.h; y++) {
      for (let x = b.x; x < b.x + b.w; x++) {
        if (x < 0 || y < 0 || x >= MAP_W || y >= MAP_H) continue;
        const onEdge = x === b.x || x === b.x + b.w - 1 || y === b.y || y === b.y + b.h - 1;
        if (onEdge) {
          // Don't overwrite water — Albert Dock blocks may extend over Mersey edge guard.
          if (g[y][x] !== T.WATER) g[y][x] = T.PAVEMENT;
        } else {
          if (g[y][x] === T.WATER) continue;
          const idx = Math.abs((x * 73856093) ^ (y * 19349663)) % d.palette.length;
          g[y][x] = d.palette[idx];
        }
      }
    }
  }
}

function bsp(r: Rect, minBlock: number, maxBlock: number, rng: () => number, out: Rect[]): void {
  // Need at least 2*minBlock + 1 to fit two children + a 1-tile road.
  const canSplitV = r.w >= 2 * minBlock + 1;
  const canSplitH = r.h >= 2 * minBlock + 1;

  if (!canSplitV && !canSplitH) {
    out.push(r);
    return;
  }

  // Force-split if oversized; otherwise random chance to keep big-ish blocks too.
  const oversized = r.w > maxBlock || r.h > maxBlock;
  if (!oversized && rng() < 0.18) {
    out.push(r);
    return;
  }

  // Pick axis: prefer the longer side
  let splitV: boolean;
  if (canSplitV && !canSplitH) splitV = true;
  else if (canSplitH && !canSplitV) splitV = false;
  else if (r.w > r.h * 1.25) splitV = true;
  else if (r.h > r.w * 1.25) splitV = false;
  else splitV = rng() < 0.5;

  if (splitV) {
    const min = minBlock, max = r.w - minBlock - 1;
    const cut = min + Math.floor(rng() * (max - min + 1));
    bsp({ x: r.x, y: r.y, w: cut, h: r.h }, minBlock, maxBlock, rng, out);
    bsp({ x: r.x + cut + 1, y: r.y, w: r.w - cut - 1, h: r.h }, minBlock, maxBlock, rng, out);
  } else {
    const min = minBlock, max = r.h - minBlock - 1;
    const cut = min + Math.floor(rng() * (max - min + 1));
    bsp({ x: r.x, y: r.y, w: r.w, h: cut }, minBlock, maxBlock, rng, out);
    bsp({ x: r.x, y: r.y + cut + 1, w: r.w, h: r.h - cut - 1 }, minBlock, maxBlock, rng, out);
  }
}

function scatterPavilions(g: number[][], rect: Rect, rng: () => number, count: number) {
  for (let i = 0; i < count; i++) {
    const w = 3 + Math.floor(rng() * 3);
    const h = 3 + Math.floor(rng() * 3);
    const x = rect.x + Math.floor(rng() * Math.max(1, rect.w - w));
    const y = rect.y + Math.floor(rng() * Math.max(1, rect.h - h));
    for (let yy = y; yy < y + h; yy++) {
      for (let xx = x; xx < x + w; xx++) {
        if (xx < 0 || yy < 0 || xx >= MAP_W || yy >= MAP_H) continue;
        const onEdge = xx === x || xx === x + w - 1 || yy === y || yy === y + h - 1;
        g[yy][xx] = onEdge ? T.PAVEMENT : T.BUILDING_A;
      }
    }
  }
}

function paintCentreLines(g: number[][]) {
  for (let y = 0; y < MAP_H; y++) {
    for (let x = 0; x < MAP_W; x++) {
      if (g[y][x] !== T.ROAD) continue;
      const left  = x > 0 && isRoad(g[y][x - 1]);
      const right = x < MAP_W - 1 && isRoad(g[y][x + 1]);
      const up    = y > 0 && isRoad(g[y - 1][x]);
      const down  = y < MAP_H - 1 && isRoad(g[y + 1][x]);
      const horizontal = left && right && !up && !down;
      const vertical   = up && down && !left && !right;
      if (horizontal && x % 2 === 0) g[y][x] = T.ROAD_LINE;
      else if (vertical && y % 2 === 0) g[y][x] = T.ROAD_LINE;
    }
  }
}

function isRoad(t: number): boolean { return t === T.ROAD || t === T.ROAD_LINE; }

/** Deterministic PRNG so the city looks the same every reload. */
function mulberry32(seed: number): () => number {
  let t = seed >>> 0;
  return () => {
    t = (t + 0x6D2B79F5) >>> 0;
    let r = Math.imul(t ^ (t >>> 15), 1 | t);
    r = (r + Math.imul(r ^ (r >>> 7), 61 | r)) ^ r;
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}
